import * as i0 from '@angular/core';
import { ViewContainerRef, Component, ViewChild, Injectable, Inject, Directive, NgModule } from '@angular/core';
import { Subject, BehaviorSubject, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { CommonModule } from '@angular/common';

class BcDialogContainerComponent {
    constructor(componentFactoryResolver) {
        this.componentFactoryResolver = componentFactoryResolver;
    }
    createModal(component) {
        this.modalContainer.clear();
        const factory = this.componentFactoryResolver.resolveComponentFactory(component);
        return this.modalContainer.createComponent(factory, 0);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcDialogContainerComponent, deps: [{ token: i0.ComponentFactoryResolver }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: BcDialogContainerComponent, selector: "ng-component", viewQueries: [{ propertyName: "modalContainer", first: true, predicate: ["modalContainer"], descendants: true, read: ViewContainerRef, static: true }], ngImport: i0, template: ` <ng-template #modalContainer></ng-template> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcDialogContainerComponent, decorators: [{
            type: Component,
            args: [{
                    template: ` <ng-template #modalContainer></ng-template> `,
                }]
        }], ctorParameters: function () { return [{ type: i0.ComponentFactoryResolver }]; }, propDecorators: { modalContainer: [{
                type: ViewChild,
                args: ['modalContainer', { read: ViewContainerRef, static: true }]
            }] } });

/* istanbul ignore file */
class BcDialogRef {
    constructor(modalContainer, modal) {
        this.modalContainer = modalContainer;
        this.modal = modal;
        this.result = new Subject();
        this.modal.instance.modalInstance = this;
    }
    close(output) {
        this.result.next(output);
        this._destroy();
    }
    dismiss(output) {
        this.result.error(output);
        this._destroy();
    }
    onResult() {
        return this.result.asObservable();
    }
    _destroy() {
        this.modal.destroy();
        this.modalContainer.destroy();
        this.result.complete();
    }
}

/* istanbul ignore file */
class BcDialog {
    close(output) {
        this.modalInstance.close(output);
    }
    dismiss(output) {
        this.modalInstance.dismiss(output);
    }
}

class BcDialogService {
    constructor(componentFactoryResolver, appRef) {
        this.componentFactoryResolver = componentFactoryResolver;
        this.appRef = appRef;
        this.setupModalContainerFactory();
    }
    /**
     * Abre un modal utilizando el componente proporcionado.
     * @param component - Componente que se mostrará en el modal.
     * @param inputs - Datos de entrada opcionales para el componente del modal.
     * @returns {BcDialogRef} Referencia al diálogo abierto.
     */
    open(component, inputs) {
        this.setupModalContainerDiv(inputs);
        const modalContainerRef = this.appRef.bootstrap(this.modalContainerFactory, this.modalContainer);
        const modalComponentRef = modalContainerRef.instance.createModal(component);
        if (inputs) {
            modalComponentRef.instance.onInjectInputs(inputs);
        }
        const modalRef = new BcDialogRef(modalContainerRef, modalComponentRef);
        this.closeEsc(modalRef);
        return modalRef;
    }
    /**
     * Configura un contenedor modal div en el documento.
     * @private
     * @ignore
     * @param inputs - Un objeto que contiene las propiedades necesarias para insertar el contenedor modal.
     * @param inputs.elementRef - El ID del elemento de referencia en el DOM donde se insertará el contenedor modal.
     * @param inputs.insertBefore - Un booleano que indica si el contenedor modal debe insertarse antes del elemento de referencia.
     *
     * Si `inputs.insertBefore` es verdadero y el elemento de referencia existe, el contenedor modal se inserta antes del elemento de referencia.
     * Si `inputs.insertBefore` es falso y el elemento de referencia existe, el contenedor modal se inserta después del elemento de referencia.
     * Si `inputs.elementRef` no está definido o el elemento de referencia no existe, el contenedor modal se agrega al final del cuerpo del documento.
     */
    setupModalContainerDiv(inputs) {
        this.modalContainer = document.createElement('div');
        if (inputs && inputs.elementRef && inputs.insertBefore && document.getElementById(inputs.elementRef)) {
            document.getElementById(inputs.elementRef).before(this.modalContainer);
        }
        else if (inputs && inputs.elementRef && document.getElementById(inputs.elementRef)) {
            document.getElementById(inputs.elementRef).after(this.modalContainer);
        }
        else {
            document
                .getElementsByTagName('body')[0]
                .appendChild(this.modalContainer);
        }
    }
    /**
     * Configura la fábrica del contenedor modal.
     *
     * Este método resuelve la fábrica del componente `BcDialogContainerComponent`
     * utilizando el `ComponentFactoryResolver` y la asigna a la propiedad
     * `modalContainerFactory`.
     * @ignore
     * @private
     */
    setupModalContainerFactory() {
        this.modalContainerFactory = this.componentFactoryResolver.resolveComponentFactory(BcDialogContainerComponent);
    }
    /**
     * Cierra el modal cuando se presiona la tecla "Escape".
     *
     * @param {any} modalRef - Referencia al modal que se desea cerrar.
     * @returns {void}
     */
    closeEsc(modalRef) {
        document.body.addEventListener("keyup", (event) => {
            if (event.code === 'Escape') {
                modalRef.close('');
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcDialogService, deps: [{ token: i0.ComponentFactoryResolver }, { token: i0.ApplicationRef }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcDialogService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcDialogService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i0.ComponentFactoryResolver }, { type: i0.ApplicationRef }]; } });

class BcComponentLimitConstanst {
    static { this.BC_COMPONENT_LIMIT_MESSAGE = 'Se excede el limite de componentes'; }
}

class ComponentLimitService {
    constructor(maxComponentsLimit) {
        this.maxComponentsLimit = maxComponentsLimit;
        this.componentCount = 0;
        this.componentCountSubject = new BehaviorSubject(0);
        this.maxComponents = maxComponentsLimit;
    }
    /**
     * Incrementa el contador de componentes, notifica a los suscriptores sobre el cambio y
     * verifica si se excedio el limite de componentes.
     * @memberof componentLimitService
     */
    incrementComponentCount() {
        this.componentCount++;
        this.componentCountSubject.next(this.componentCount);
        if (this.componentCount > this.maxComponents) {
            throw new Error(BcComponentLimitConstanst.BC_COMPONENT_LIMIT_MESSAGE);
        }
    }
    /**
     * Devuelve el conteo actualizado de los componente en la aplicacion
     * @memberof Observable
     */
    getComponentCount() {
        return this.componentCountSubject;
    }
    /**
     * Setea los shortcuts cada que se selecciona una nueva cantidad
     * @memberof componentLimitService
     */
    resetShortcuts() {
        this.componentCount = 0;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ComponentLimitService, deps: [{ token: 'maxComponentsLimit' }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ComponentLimitService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ComponentLimitService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['maxComponentsLimit']
                }] }]; } });

class BcDynamicHostDirective {
    constructor(viewContainerRef) {
        this.viewContainerRef = viewContainerRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcDynamicHostDirective, deps: [{ token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: BcDynamicHostDirective, selector: "[bcBcDynamicHost]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcDynamicHostDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[bcBcDynamicHost]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ViewContainerRef }]; } });

class BcDynamicHostService {
    constructor(cfr) {
        this.cfr = cfr;
    }
    forChild(vcr, cl) {
        return from(cl.loadChildren()).pipe(map((component) => this.cfr.resolveComponentFactory(component)), map(componentFactory => vcr.createComponent(componentFactory)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcDynamicHostService, deps: [{ token: i0.ComponentFactoryResolver }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcDynamicHostService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcDynamicHostService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i0.ComponentFactoryResolver }]; } });

class BcServicesModule {
    static forRoot() {
        return {
            ngModule: BcServicesModule,
            providers: [BcDialogService, BcDynamicHostService, { provide: 'maxComponentsLimit', useValue: 8 }],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcServicesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: BcServicesModule, declarations: [BcDialogContainerComponent, BcDynamicHostDirective], imports: [CommonModule], exports: [BcDialogContainerComponent, BcDynamicHostDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcServicesModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcServicesModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [BcDialogContainerComponent, BcDynamicHostDirective],
                    exports: [BcDialogContainerComponent, BcDynamicHostDirective]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { BcDialog, BcDialogContainerComponent, BcDialogRef, BcDialogService, BcDynamicHostDirective, BcDynamicHostService, BcServicesModule, ComponentLimitService };
